/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, {
  useState,
  ReactElement,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBriefcase,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import RequestHandler from 'octa-front-sdk/dist/RequestHandler';
import WizardContext from 'octa-front-sdk/dist/contexts/WizardContext';
import Configs from 'octa-front-sdk/dist/Configs';

import { Neighbourhood } from '../../../../types/Neighbourhood';

import { Address } from '../../../../controllers/AddressController';
import OrganizationContext from '../../../../contexts/OrganizationContext';
import GlobalContext from '../../../../contexts/GlobalContext';

import Input from '../../../Input';
import SelectInput from '../../../SelectInput';
import BtnWithLoad from '../../../BtnWithLoad';

import { Container, Names, NotServedWarning } from './styles';

enum NameOptions {
  home = 'Casa',
  work = 'Trabalho',
  others = 'Outros',
}
interface IProps {
  currentAddress: Address;
  isEditing: boolean;
  setShowForm: (value: boolean) => void;
  onSave?: (newAddress?: Address) => void;
  onRemove?: () => void;
  shouldGoBackOnSave?: boolean;
}

const AddressForm: React.FC<IProps> = (props: IProps) => {
  const {
    currentAddress,
    isEditing,
    setShowForm,
    onSave,
    onRemove,
    shouldGoBackOnSave,
  } = props;

  const formRef = useRef<FormHandles>(null);

  const { organizationInfo } = useContext(OrganizationContext);
  const { chosenAddress, setChosenAddress } = useContext(GlobalContext);
  const { goToPreviousStep } = useContext(WizardContext);

  const hasNeighbourhood = organizationInfo!.neighbourhoods!.length > 0;

  const [nameOption, setNameOption] = useState<NameOptions>(NameOptions.home);
  const [shouldAskForName, setShouldAskForName] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDeleteBtn, setIsLoadingDeleteBtn] = useState(false);
  const [showNotServedMessage, setShowNotServedMessage] = useState(false);
  const [showNeighbourhoodSelect, setShowNeighbourhoodSelect] = useState(false);
  const [showNeighbourhoodInput, setShowNeighbourhoodInput] = useState(false);
  const [
    neighbourhoodSelected,
    setNeighbourhoodSelected,
  ] = useState<Neighbourhood>();

  const nameLabelsAndIcons = [
    { label: NameOptions.home, icon: faHome },
    { label: NameOptions.work, icon: faBriefcase },
    { label: NameOptions.others, icon: faMapMarkerAlt },
  ];

  const checkIfNeighbourhoodMatch = useCallback(() => {
    if (hasNeighbourhood && currentAddress?.neighbourhood) {
      const { neighbourhood } = currentAddress;
      const { neighbourhoods } = organizationInfo!;

      const matched = neighbourhoods.find(nb => {
        const nbWithoutAccents = nb.name
          .normalize('NFD')
          .replace(/[\u0300-\u036F]/g, '')
          .toLowerCase();

        const neighbourhoodWithoutAccents = neighbourhood
          .normalize('NFD')
          .replace(/[\u0300-\u036F]/g, '')
          .toLowerCase();

        return nbWithoutAccents === neighbourhoodWithoutAccents;
      });

      if (matched) setNeighbourhoodSelected(matched);

      return matched;
    }
  }, [currentAddress, hasNeighbourhood, organizationInfo]);

  useEffect(() => {
    async function checkNeighbourhoodIsServed() {
      if (!currentAddress.neighbourhood && hasNeighbourhood) {
        currentAddress.neighbourhood = '';
        return setShowNeighbourhoodSelect(true);
      }

      // TODO: conversar com Gabriel sobre obrigatoriedade do campo de neighbourhood
      const response = await RequestHandler.post(
        'user/address/deliverydetails',
        {
          neighbourhood: currentAddress.neighbourhood
            ? currentAddress.neighbourhood
            : 'sem bairro',
          location: currentAddress.location,
        },
      );

      const { isServed } = response.data;

      if (isServed && !hasNeighbourhood) {
        formRef.current?.setFieldValue('neighbourhood', '');
        return setShowNeighbourhoodInput(true);
      }

      if (!isServed && !hasNeighbourhood) return setShowNotServedMessage(true);

      if (isServed) return setShowNeighbourhoodSelect(false);

      if (!isServed && !!checkIfNeighbourhoodMatch())
        return setShowNeighbourhoodSelect(false);

      if (!isServed && !checkIfNeighbourhoodMatch()) {
        currentAddress.neighbourhood = '';
        return setShowNeighbourhoodSelect(true);
      }
    }

    checkNeighbourhoodIsServed();
  }, [checkIfNeighbourhoodMatch, currentAddress, hasNeighbourhood]);

  function renderSelectNeighbourhood() {
    return showNeighbourhoodSelect ? (
      <SelectInput name="neighbourhood" label="Bairro*">
        {organizationInfo?.neighbourhoods
          .sort((a, b): number => a.name.localeCompare(b.name))
          .map(
            (n, i): ReactElement => (
              <option key={`option-${i}`} value={n.name}>
                {n.name}
              </option>
            ),
          )}
      </SelectInput>
    ) : (
      <Input
        name="neighbourhood"
        label="Bairro*"
        disabled={!showNeighbourhoodInput}
      />
    );
  }

  useEffect(() => {
    formRef.current?.setFieldValue(
      'neighbourhood',
      currentAddress.neighbourhood,
    );
  }, [currentAddress, showNeighbourhoodSelect]);

  async function handleSubmit(data: Address) {
    if (nameOption !== NameOptions.others) data.name = nameOption;
    if (neighbourhoodSelected) data.neighbourhood = neighbourhoodSelected.name;

    data.country = 'BR';
    data.location = currentAddress.location;

    setIsLoading(true);

    try {
      const response = await RequestHandler.request({
        method: isEditing ? 'PUT' : 'POST',
        url: isEditing ? `user/address/${currentAddress.id}` : 'user/address',
        baseURL: Configs.REACT_APP_API_URL,
        data,
      });

      const { id } = response.data;

      if (onSave) {
        onSave({ ...data, id });
      }

      if (shouldGoBackOnSave) goToPreviousStep();
    } finally {
      setIsLoading(false);
    }
  }

  function removeAddress(): void {
    if (window.confirm('Confirmar remoção de endereço?') && currentAddress) {
      const { id } = currentAddress;

      setIsLoadingDeleteBtn(true);

      RequestHandler.delete(`user/address/${id}`)
        .then((): void => {
          const storedAddress = localStorage.getItem('chosenAddress');

          if (id && storedAddress && JSON.parse(storedAddress).id === +id)
            localStorage.removeItem('chosenAddress');
        })
        .finally((): void => {
          setIsLoadingDeleteBtn(false);

          if (onRemove) onRemove();
        });

      if (
        chosenAddress &&
        JSON.stringify(chosenAddress.id) === currentAddress.id
      ) {
        setChosenAddress(undefined);
      }
    }
  }

  return (
    <Container>
      {showNotServedMessage ? (
        <NotServedWarning>
          <span>Desculpe, seu endereço não é atendido!</span>
          <button type="button" onClick={() => setShowForm(false)}>
            Selecionar outro endereço
          </button>
        </NotServedWarning>
      ) : (
        <Form
          initialData={currentAddress}
          ref={formRef}
          onSubmit={handleSubmit}
        >
          <h2>Confirme os dados abaixo e ajuste para o endereço correto.</h2>
          <div className="form-line">
            <Input name="street" label="Rua*" />
            <Input name="number" label="Número*" />
          </div>
          <div className="form-line">
            {renderSelectNeighbourhood()}
            <Input name="complement" label="Complemento*" />
          </div>
          <div className="form-line state-line">
            <Input name="state" label="Estado*" />
            <Input name="city" label="Cidade*" />
          </div>
          <Names>
            {nameLabelsAndIcons.map(
              (n, index): ReactElement => (
                <div
                  key={index}
                  className={n.label === nameOption ? 'active' : ''}
                  onClick={(): void => {
                    setNameOption(n.label);

                    setShouldAskForName(n.label === NameOptions.others);
                  }}
                >
                  <FontAwesomeIcon icon={n.icon} size="2x" />

                  <span>{n.label}</span>
                </div>
              ),
            )}
          </Names>

          {shouldAskForName && (
            <Input
              name="name"
              label="Nome do lugar"
              placeholder="Casa, trabalho etc."
            />
          )}
          <BtnWithLoad
            className="submit-btn"
            type="submit"
            name={`${isEditing ? 'Editar' : 'Salvar'} endereço`}
            loading={isLoading}
          />
          {isEditing && (
            <BtnWithLoad
              type="button"
              name="Apagar endereço"
              className="delete-btn"
              onClick={removeAddress}
              loading={isLoadingDeleteBtn}
            />
          )}
        </Form>
      )}
    </Container>
  );
};

export default AddressForm;
